import React from 'react';
import { Link } from '@/components/LocaleLink';
import SEO from '@/components/SEO';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import useScrollReveal from '@/hooks/useScrollReveal';
import { trackEvent } from '@/utils/analytics';
import { useLanguage } from '@/contexts/language/LanguageContext';
import { howItWorksCopy } from '@/contexts/language/translations/pages/howItWorks';

/* ── The Stratodata Stairway — 6 levels of data maturity ──
   Copy (EN + SK) lives in src/contexts/language/translations/pages/howItWorks.ts */
const STAIRWAY_FLAGS = [
  { num: 1, here: true },
  { num: 2, here: true },
  { num: 3 },
  { num: 4 },
  { num: 5 },
  { num: 6, destination: true },
] as { num: number; here?: boolean; destination?: boolean }[];

const HowItWorks = () => {
  useScrollReveal();
  const { language } = useLanguage();
  const c = howItWorksCopy[language];
  const STAIRWAY = STAIRWAY_FLAGS.map((f, i) => ({ ...f, ...c.stairway.levels[i] }));
  const STEPS = c.process.steps;
  const PROOF = c.proof.items;

  return (
    <div className="flex flex-col min-h-screen">
      <SEO
        title={c.seoTitle}
        description={c.seoDescription}
        canonical="/how-it-works"
      />
      <Navigation />
      <main className="index-page flex-1">

        {/* Hero */}
        <section className="relative pt-28 pb-20 md:pt-36 md:pb-28 overflow-hidden bg-white">
          <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-[#FFF7EC] rounded-full filter blur-3xl opacity-60" />
          <div className="absolute bottom-20 right-0 w-[400px] h-[400px] bg-[#E0E7FF] rounded-full filter blur-3xl opacity-40" />

          <div className="container-custom relative z-10 reveal">
            <div className="max-w-4xl mx-auto text-center">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF6B35] mb-5">{c.hero.eyebrow}</p>
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-[#2A1E47] mb-6 leading-[1.08]">
                {c.hero.title}
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10">
                {c.hero.subtitle}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/book-a-call"
                  className="inline-flex items-center justify-center px-8 py-3 rounded-lg bg-[#FF6B35] text-white font-medium hover:bg-[#FF6B35]/90 transition-colors"
                >
                  {c.hero.ctaPrimary}
                </Link>
                <button
                  onClick={() => {
                    trackEvent('how_it_works_steps', 'engagement', 'hero_cta_click');
                    document.getElementById('steps')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className="inline-flex items-center gap-2 px-8 py-3 rounded-lg border-2 border-[#2A1E47]/20 text-[#2A1E47] font-medium hover:border-[#2A1E47]/40 hover:bg-[#2A1E47]/5 transition-colors"
                >
                  {c.hero.ctaSecondary}
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* The Stratodata Stairway */}
        <section className="py-16 md:py-24 bg-[#FAFAFA]">
          <div className="container-custom reveal">
            <div className="max-w-3xl mx-auto text-center mb-14">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF6B35] mb-4">{c.stairway.eyebrow}</p>
              <h2 className="text-3xl md:text-4xl font-bold text-[#2A1E47]">
                {c.stairway.title}
              </h2>
            </div>

            <div className="overflow-x-auto -mx-4 px-4 pb-2">
              <div className="grid grid-cols-6 gap-3 min-w-[900px] relative">
                <div className="absolute left-4 right-4 top-[22px] h-px bg-gray-200" />
                {STAIRWAY.map((s) => (
                  <div key={s.num} className="relative flex flex-col gap-2">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-[#FF6B35] h-3">
                      {s.here ? c.stairway.youAreHere : s.destination ? c.stairway.destination : ' '}
                    </span>
                    <span
                      className={
                        'relative z-10 w-11 h-11 rounded-full flex items-center justify-center text-sm font-bold border ' +
                        (s.here || s.destination
                          ? 'bg-[#FF6B35] border-[#FF6B35] text-white'
                          : 'bg-white border-gray-200 text-muted-foreground')
                      }
                    >
                      {s.num}
                    </span>
                    <h3 className="font-bold text-sm text-[#2A1E47]">{s.title}</h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">{s.detail}</p>
                  </div>
                ))}
              </div>
            </div>

            <p className="text-center text-sm text-muted-foreground max-w-2xl mx-auto mt-10">
              {c.stairway.noteBefore}<span className="font-semibold text-[#2A1E47]">{c.stairway.noteHighlight}</span>{c.stairway.noteAfter}
            </p>
          </div>
        </section>

        {/* The 3-step process */}
        <section id="steps" className="py-20 md:py-28 bg-white">
          <div className="container-custom reveal">
            <div className="max-w-2xl mb-14">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF6B35] mb-4">{c.process.eyebrow}</p>
              <h2 className="text-3xl md:text-4xl font-bold text-[#2A1E47]">
                {c.process.title}
              </h2>
            </div>

            <div className="max-w-3xl space-y-10">
              {STEPS.map((step, i) => (
                <div key={step.tag} className="grid grid-cols-[auto_1fr] gap-6 md:gap-8">
                  <span className="text-3xl md:text-4xl font-bold text-[#2A1E47]/10 leading-none pt-1">
                    {`0${i + 1}`}
                  </span>
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#FF6B35] mb-2">{step.tag}</p>
                    <h3 className="text-xl font-bold text-[#2A1E47] mb-3">{step.title}</h3>
                    <p className="text-muted-foreground leading-relaxed mb-4 max-w-xl">{step.description}</p>
                    <div className="flex flex-wrap gap-6 text-sm">
                      <div>
                        <span className="block text-[10px] font-bold uppercase tracking-wider text-[#2A1E47]/60 mb-1">{c.process.youLabel}</span>
                        <p className="text-muted-foreground max-w-xs">{step.you}</p>
                      </div>
                      <div>
                        <span className="block text-[10px] font-bold uppercase tracking-wider text-[#FF6B35] mb-1">{c.process.usLabel}</span>
                        <p className="text-muted-foreground max-w-xs">{step.us}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <p className="text-sm text-muted-foreground mt-10">
              {c.process.browsePrompt}{' '}
              <Link to="/#solutions" className="text-[#FF6B35] font-medium hover:underline">
                {c.process.browseLink}
              </Link>
              .
            </p>
          </div>
        </section>

        {/* Talk to your data — dark, in practice */}
        <section className="py-20 md:py-28 bg-[#2A1E47]">
          <div className="container-custom reveal">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-center max-w-5xl mx-auto">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF6B35] mb-4">{c.talk.eyebrow}</p>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">
                  {c.talk.title}
                </h2>
                <p className="text-white/60 leading-relaxed">
                  {c.talk.text}
                </p>
              </div>
              <div className="rounded-2xl bg-black/20 border border-white/10 p-6 font-mono text-sm leading-relaxed">
                <p className="text-[#FF6B35]">
                  <span className="text-white/30">{`${c.talk.askLabel} `}</span>
                  {c.talk.question}
                </p>
                <div className="flex flex-wrap items-center gap-2 my-4 text-white/70 text-xs">
                  <span className="rounded bg-white/5 border border-white/10 px-2 py-1">{c.talk.flowQuery}</span>
                  <span className="text-[#FF6B35]">&rarr;</span>
                  <span className="rounded bg-white/5 border border-white/10 px-2 py-1">{c.talk.flowLlm}</span>
                  <span className="text-[#FF6B35]">&rarr;</span>
                  <span className="rounded bg-white/5 border border-white/10 px-2 py-1">{c.talk.flowWarehouse}</span>
                </div>
                <p className="text-emerald-300">
                  <span className="text-white/30">{`${c.talk.getLabel} `}</span>
                  {c.talk.answer}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Proof points */}
        <section className="py-20 md:py-28 bg-white">
          <div className="container-custom reveal">
            <div className="max-w-2xl mb-12">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF6B35] mb-4">{c.proof.eyebrow}</p>
              <h2 className="text-3xl md:text-4xl font-bold text-[#2A1E47]">
                {c.proof.title}
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 border border-gray-100 divide-x divide-y md:divide-y-0 divide-gray-100">
              {PROOF.map((p) => (
                <div key={p.label} className="p-6 bg-[#FAFAFA]">
                  <span className="block text-3xl font-bold text-[#2A1E47] mb-1">{p.num}</span>
                  <span className="text-xs text-muted-foreground leading-snug">{p.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA banner */}
        <section className="relative overflow-hidden py-20">
          <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B35] via-[#FF7F27] to-[#FF4500] rounded-3xl" />
          <div className="container-custom relative z-10">
            <div className="max-w-3xl mx-auto text-center pt-16 pb-8 px-4 reveal">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-white leading-tight">
                {c.cta.title}
              </h2>
              <p className="text-lg md:text-xl text-white/90 mb-10 max-w-xl mx-auto">
                {c.cta.text}
              </p>
              <div className="flex justify-center">
                <Link
                  to="/book-a-call"
                  className="inline-flex items-center justify-center px-8 py-4 rounded-lg bg-[#2a1e48] hover:bg-[#2a1e48]/90 text-white text-base font-medium transition-all duration-300 shadow-lg border border-white/10"
                >
                  {c.cta.button}
                </Link>
              </div>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
};

export default HowItWorks;
